import React, { useContext, useCallback, Suspense, lazy } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import _ from "lodash";
import DOMPurify from "dompurify";
import { CKEditorContext } from "@ckeditor/ckeditor5-react";
import {
  BalloonEditor,
  Context,
  ContextWatchdog,
  Essentials,
  Font,
  Heading,
  Code,
  Bold,
  Italic,
  Paragraph,
  Underline,
  Strikethrough,
  BlockQuote,
  List,
  Indent,
  IndentBlock,
  Link,
  AutoImage,
  Image,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageCaption,
  PictureEditing,
  MediaEmbed,
  FullPage,
  Alignment,
  RemoveFormat,
  Autoformat,
  AutoLink,
  Base64UploadAdapter,
  SourceEditing,
  GeneralHtmlSupport,
} from "ckeditor5";
import AppContext from "../../AppContext";
import ScrollToTop from "../common/ScrollToTop";
import Breadcrumb from "../Breadcrumb";
import leftArrowBlue from "../../assets/icons/arrow-left-blue.png";
import { langComponent } from "../../constants/index.jsx";
import Layout from "../../components/Support/Layout";

const CKEditor = lazy(() =>
  import("@ckeditor/ckeditor5-react").then((module) => ({
    default: module.CKEditor,
  }))
);
const Loading = () => null;

const Page = ({ category, route, content }) => {
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.support);
  const navigate = useNavigate();
  return (
    <div className="faqs-support-renderer-layout">
      <div className="faqs-support-renderer">
        <ScrollToTop />
        <Breadcrumb
          config={[
            {
              path: "/support",
              breadcrumb: "Support",
              props: { pathname: `/support/faqs` },
            },
            {
              path: `/support/${category.route}`,
              breadcrumb: `${_.upperFirst(category.name)}`,
              props: { pathname: `/support/${category.route}` },
            },
            {
              path: "/support",
              breadcrumb: `${_.upperFirst(category.name)}`,
              props: { pathname: `/support/${route}` },
            },
          ]}
        />
        <div
          className="faqs-support-renderer-return"
          onClick={() => navigate(-1)}
        >
          <img alt="return" src={leftArrowBlue} width={24} />
          {Translation("Back")}
        </div>
        <div className="faqs-support-renderer-content">
          <Suspense fallback={<Loading />}>
            <CKEditorContext
              context={Context}
              contextWatchdog={ContextWatchdog}
            >
              <CKEditor
                editor={BalloonEditor}
                disabled
                config={{
                  placeholder: "Type or paste your content here!",
                  plugins: [
                    Essentials,
                    Font,
                    Heading,
                    Code,
                    Bold,
                    Italic,
                    Paragraph,
                    Underline,
                    Strikethrough,
                    BlockQuote,
                    List,
                    Indent,
                    IndentBlock,
                    Link,
                    AutoImage,
                    Image,
                    ImageInsert,
                    ImageResize,
                    ImageStyle,
                    ImageToolbar,
                    ImageCaption,
                    PictureEditing,
                    MediaEmbed,
                    FullPage,
                    Alignment,
                    RemoveFormat,
                    Autoformat,
                    AutoLink,
                    Base64UploadAdapter,
                    SourceEditing,
                    GeneralHtmlSupport,
                  ],
                  toolbar: [],
                  htmlSupport: {
                    allow: [
                      {
                        name: /.*/,
                        attributes: true,
                        classes: true,
                        styles: true,
                      },
                    ],
                    disallow: [
                      {
                        attributes: [
                          { key: /^on(.*)/i, value: true },
                          {
                            key: /.*/,
                            value:
                              /(\b)(on\S+)(\s*)=|javascript:|(<\s*)(\/*)script/i,
                          },
                          {
                            key: /.*/,
                            value: /data:(?!image\/(png|jpeg|gif|webp))/i,
                          },
                        ],
                      },
                      { name: "script" },
                    ],
                  },
                }}
                data={DOMPurify.sanitize(content)}
              />
            </CKEditorContext>
          </Suspense>
        </div>
        <div
          className="faqs-support-renderer-return"
          onClick={() => navigate(-1)}
        >
          <img alt="return" src={leftArrowBlue} width={24} />
          {Translation("Back")}
        </div>
      </div>
    </div>
  );
};

const TabLayout = React.memo(({ category, subCategory }) => {
  const pathPrefix = `/support/${category.route}`;

  const tabs = subCategory.map((d, i) => ({
    id: i,
    title: d.name,
    path: `${pathPrefix}/${d.route}`,
    icon: d.icon,
  }));

  return (
    <div className="faqs-support-join">
      <div className={`faqs-support-join-content`}>
        <Layout title={`${category.name}`} tabs={tabs}>
          {/* The content will now be handled by the outer Routes */}
        </Layout>
      </div>
    </div>
  );
});

const Support = () => {
  const { faqs, getLang, lang } = useContext(AppContext);
  const FAQCategory = faqs?.category || [];
  const FAQSubCategory = faqs?.subCategory || [];
  const FAQContent = faqs?.content;

  const createCategoryRoutes = useCallback(
    (category) => {
      const subCategoryItems = FAQSubCategory.filter(
        (sub) => sub.category === category.value
      );

      const data = _.filter(
        FAQContent,
        (content) =>
          content.category === category.value && lang === content.language
      );

      const fallbackData = _.filter(
        FAQContent,
        (content) =>
          content.category === category.value && content.language === "th"
      );

      // Combine data if there's only one entry in the primary language
      const combinedData =
        data.length === 1
          ? [...data, ...fallbackData]
          : data.length > 0
          ? data
          : fallbackData;

      if (!combinedData.length) {
        return (
          <Route
            key={category.route}
            path={category.route}
            element={<div>No content available.</div>}
          />
        );
      }

      if (subCategoryItems.length > 0) {
        return (
          <>
            <Route
              key={category.route}
              path={`${category.route}/*`}
              element={
                <TabLayout category={category} subCategory={subCategoryItems} />
              }
            ></Route>
            {combinedData.map((contentData) => {
              const subCat = subCategoryItems.find(
                (sub) => sub.value === contentData.subCategory
              );
              return (
                <Route
                  key={subCat.route}
                  path={`${category.route}/${subCat.route}`}
                  element={
                    <Page
                      category={category}
                      route={`${category.route}/${subCat.route}`}
                      content={contentData.content}
                    />
                  }
                />
              );
            })}
          </>
        );
      } else {
        const singleData = combinedData[0];
        return (
          <Route
            key={category.route}
            path={category.route}
            element={
              <Page
                category={category}
                route={category.route}
                content={singleData?.content}
              />
            }
          />
        );
      }
    },
    [FAQSubCategory, FAQContent, lang]
  );

  return <Routes>{FAQCategory.map(createCategoryRoutes)}</Routes>;
};

export default Support;
